import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import Model from 'flarum/common/Model';
import Discussion from 'flarum/common/models/Discussion';
import { extend, override } from 'flarum/common/extend';
import Button from 'flarum/common/components/Button';
import LinkButton from 'flarum/common/components/LinkButton';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import SelectDropdown from 'flarum/common/components/SelectDropdown';
import DiscussionControls from 'flarum/forum/utils/DiscussionControls';
import DiscussionComposer from 'flarum/forum/components/DiscussionComposer';
import DiscussionList from 'flarum/forum/components/DiscussionList';
import DiscussionListItem from 'flarum/forum/components/DiscussionListItem';
import DiscussionListState from 'flarum/forum/states/DiscussionListState';
import IndexPage from 'flarum/forum/components/IndexPage';
import DiscussionPage from 'flarum/forum/components/DiscussionPage';
import PostControls from 'flarum/forum/utils/PostControls';
import PostStream from 'flarum/forum/components/PostStream';
import EditPostComposer from 'flarum/forum/components/EditPostComposer';
import ReplyComposer from 'flarum/forum/components/ReplyComposer';
import CommentPost from 'flarum/forum/components/CommentPost';
import Search from 'flarum/forum/components/Search';
import DiscussionsSearchItem from 'flarum/forum/components/DiscussionsSearchItem';
import TextEditor from 'flarum/common/components/TextEditor';
import Post from 'flarum/common/models/Post';
import FuzzyDiscussionsSearchSource from './components/FuzzyDiscussionsSearchSource';
import MediaComposer from './components/MediaComposer';
import MediaGallery from './components/MediaGallery';
import MobileBottomNav from './components/MobileBottomNav';
import MobileSearchPage from './components/MobileSearchPage';
import TopicComposer from './components/TopicComposer';
import TopicList from './components/TopicList';
import ReplyGroup from './components/ReplyGroup';
import { registerDiscussionList } from './discussionNavigation';

const IMAGE_ONLY_CONTENT = '\u200B';
const LEGACY_IMAGE_ONLY_CONTENT = '<!-- space-pocket-posts:image-only -->';
const cloneMedia = (media) => (Array.isArray(media) ? media.map((item) => ({ ...item })) : []);
const cloneTopics = (topics) => (Array.isArray(topics) ? topics.map((topic) => String(topic)) : []);
const stripLegacyImageOnlyContent = (value) => {
  if (typeof value === 'string') return value.split(LEGACY_IMAGE_ONLY_CONTENT).join('');

  if (Array.isArray(value)) {
    for (let index = value.length - 1; index >= 0; index -= 1) {
      const child = value[index];
      const cleaned = stripLegacyImageOnlyContent(child);

      if (cleaned === '' || cleaned === null || cleaned === undefined) {
        value.splice(index, 1);
      } else {
        value[index] = cleaned;
      }
    }

    return value;
  }

  if (value && typeof value === 'object' && 'children' in value) {
    value.children = stripLegacyImageOnlyContent(value.children);
  }

  return value;
};
const cloneTopicLinks = (links, topics = []) => {
  if (!Array.isArray(links) || !links.length) return topics.map((name) => ({ id: null, name }));

  return links
    .map((topic) => ({
      id: topic && topic.id !== null && topic.id !== undefined ? String(topic.id) : null,
      name: String(topic && topic.name ? topic.name : ''),
    }))
    .filter((topic) => topic.name);
};
const POST_MENTION_PATTERN = /^@"([^"]+)"#p\d+$/;
const TOPIC_SHORTCUT_EVENT = 'space-pocket-posts:open-topics';
const IMAGE_REQUIRED_TRANSLATION_KEY = 'space-pocket-posts.forum.composer.image_required';
const FORUM_PRODUCT_SHARE_PARAM = 'spacePocketShare';
const FORUM_PRODUCT_SHARE_MAX_IMAGES = 9;
const INDEX_DISCUSSIONS_LINK_TRANSLATION_KEY = 'core.forum.index.all_discussions_link';
const INDEX_NEW_DISCUSSION_TRANSLATION_KEY = 'core.forum.index.start_discussion_button';
const INDEX_TOP_SORT_TRANSLATION_KEY = 'core.forum.index_sort.top_button';
const UNKNOWN_USER_LABEL_PATTERN = /\[(?:未知用户|Unknown user)\]/gi;
const MASONRY_LIST_SELECTOR = '.SpacePocketPosts-userDiscussionCards';
const MASONRY_CARD_SELECTOR = ':scope > .CardsListItem';
const MASONRY_ROW_HEIGHT = 8;
const WECHAT_SDK_URL = 'https://res.wx.qq.com/open/js/jweixin-1.6.0.js';
const WECHAT_SHARE_API_LIST = [
  'updateAppMessageShareData',
  'updateTimelineShareData',
  'onMenuShareAppMessage',
  'onMenuShareTimeline',
];
const AUTO_LOAD_MORE_SELECTOR = '.DiscussionList:has(.SpacePocketPosts-userDiscussionCards) > .DiscussionList-loadMore';
const AUTO_LOAD_MARGIN = 400;
const DESKTOP_AUTO_LOAD_MIN_MARGIN = 2400;
const DESKTOP_AUTO_LOAD_VIEWPORTS = 3;
const AUTO_LOAD_STATUS_POLL_INTERVAL = 100;
const AUTO_LOAD_STATUS_TIMEOUT = 15000;
const POPULAR_TOPICS_CACHE_TTL = 60 * 1000;
const popularTopicsCache = {
  topics: [],
  loadedAt: 0,
  request: null,
};
const wechatShareState = {
  scriptPromise: null,
  requestId: 0,
  lastKey: null,
  data: null,
  discussion: null,
  wx: null,
  ready: false,
  configStarted: false,
  signatureUrl: null,
  pendingSignatureUrl: null,
};

const isWechatBrowser = () => typeof navigator !== 'undefined' && /MicroMessenger/i.test(navigator.userAgent || '');

const forumAttribute = (key, fallback = null) => {
  const forum = app && app.forum;

  return forum && typeof forum.attribute === 'function' ? forum.attribute(key) : fallback;
};

const absoluteUrl = (value) => {
  if (!value || typeof window === 'undefined') return '';

  try {
    return new URL(String(value), window.location.origin).toString();
  } catch (error) {
    return '';
  }
};

const sharePageUrl = () => (typeof window === 'undefined' ? '' : window.location.href.split('#')[0]);

const decodeForumProductShare = (encoded) => {
  if (!encoded || typeof encoded !== 'string' || encoded.length > 16000) return null;

  try {
    const normalized = encoded.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
    const binary = atob(padded);
    const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
    const sharedImages = JSON.parse(new TextDecoder().decode(bytes));

    if (!Array.isArray(sharedImages)) {
      return null;
    }

    const images = Array.from(
      new Set(
        sharedImages
          .map((image) => String(image || '').trim())
          .filter((image) => {
            if (image.length > 2048) return false;

            try {
              const url = new URL(image);
              return url.protocol === 'http:' || url.protocol === 'https:';
            } catch (error) {
              return false;
            }
          })
      )
    ).slice(0, FORUM_PRODUCT_SHARE_MAX_IMAGES);

    if (!images.length) return null;

    return images;
  } catch (error) {
    return null;
  }
};

const readForumProductShare = () => {
  if (typeof window === 'undefined') return null;

  try {
    return decodeForumProductShare(new URL(window.location.href).searchParams.get(FORUM_PRODUCT_SHARE_PARAM));
  } catch (error) {
    return null;
  }
};

const clearForumProductShareParam = () => {
  if (typeof window === 'undefined' || !window.history || !window.location) return;

  try {
    const url = new URL(window.location.href);
    url.searchParams.delete(FORUM_PRODUCT_SHARE_PARAM);
    window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);
  } catch (error) {
    // An invalid share URL must not interfere with normal forum navigation.
  }
};

const forumProductShareImageType = (image) => {
  try {
    const pathname = new URL(image).pathname.toLowerCase();
    if (pathname.endsWith('.png')) return 'image/png';
    if (pathname.endsWith('.webp')) return 'image/webp';
    if (pathname.endsWith('.gif')) return 'image/gif';
    if (pathname.endsWith('.avif')) return 'image/avif';
  } catch (error) {
    // Fall back to JPEG for image URLs without a recognizable extension.
  }

  return 'image/jpeg';
};

const forumProductShareHash = (value) => {
  let hash = 0;

  for (let index = 0; index < value.length; index += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(index);
    hash |= 0;
  }

  return Math.abs(hash).toString(36);
};

const forumProductShareMedia = (images) => {
  return images.map((url, index) => ({
    url,
    uuid: `mall-share-${index}-${forumProductShareHash(url)}`.slice(0, 128),
    name: `mall-image-${index + 1}`,
    type: forumProductShareImageType(url),
  }));
};

const installForumProductShare = () => {
  if (app.spacePocketForumProductShareInstalled) return;

  app.spacePocketForumProductShareInstalled = true;

  const attemptOpen = (attempt = 0) => {
    if (app.spacePocketForumProductShareHandled) return;

    const images = readForumProductShare();
    if (!images) {
      if (typeof window !== 'undefined') {
        try {
          if (new URL(window.location.href).searchParams.has(FORUM_PRODUCT_SHARE_PARAM)) {
            clearForumProductShareParam();
          }
        } catch (error) {
          // Ignore malformed browser URLs.
        }
      }
      return;
    }

    const user = app.session && app.session.user;
    if (!user || !app.composer || typeof app.composer.load !== 'function' || typeof app.composer.show !== 'function') {
      if (attempt < 100 && typeof window !== 'undefined') {
        window.setTimeout(() => attemptOpen(attempt + 1), 100);
      }
      return;
    }

    app.spacePocketForumProductShareHandled = true;
    clearForumProductShareParam();

    app.spacePocketForumProductShareMedia = forumProductShareMedia(images);
    app.composer.load(DiscussionComposer, { user });
    app.composer.show();
  };

  attemptOpen();
};

const canonicalShareUrl = (value) => {
  if (!value) return '';

  try {
    const url = new URL(value);
    ['from', 'isappinstalled', 'scene', 'subscene', 'clicktime', 'enterid']
      .forEach((key) => url.searchParams.delete(key));
    return url.toString();
  } catch (error) {
    return value;
  }
};

const stripMarkup = (value) => {
  if (typeof value !== 'string') return '';

  const container = document.createElement('div');
  container.innerHTML = value;

  return (container.textContent || '').replace(/\s+/g, ' ').trim();
};

const pageShareImage = () => {
  if (typeof document === 'undefined') return '';

  const image = document.querySelector('.SpacePocketPosts-gallery img');

  return image ? absoluteUrl(image.currentSrc || image.getAttribute('src')) : '';
};

const shareTitleWithSite = (title, siteTitle) => {
  const pageTitle = String(title || '').trim();
  const suffix = String(siteTitle || '').trim();

  if (!suffix || !pageTitle || pageTitle === suffix || pageTitle.endsWith(` - ${suffix}`)) {
    return pageTitle || suffix;
  }

  return `${pageTitle} - ${suffix}`;
};

const updateShareMeta = (data) => {
  if (data.title && typeof document !== 'undefined') document.title = data.title;

  const values = {
    'og:title': data.title,
    'og:description': data.description,
    'og:url': data.link,
    'og:image': data.imgUrl,
    description: data.description,
  };

  Object.entries(values).forEach(([name, content]) => {
    if (!content) return;

    const selector = name === 'description' ? 'meta[name="description"]' : `meta[property="${name}"]`;
    let meta = document.head.querySelector(selector);

    if (!meta) {
      meta = document.createElement('meta');

      if (name === 'description') meta.setAttribute('name', name);
      else meta.setAttribute('property', name);

      document.head.appendChild(meta);
    }

    meta.setAttribute('content', content);
  });
};

const discussionShareData = (discussion) => {
  // WeChat signs the URL shown by the webview at the moment wx.config runs.
  const currentUrl = sharePageUrl();
  const signatureUrl = currentUrl;
  const link = canonicalShareUrl(signatureUrl);
  const siteTitle = String(forumAttribute('title', '') || document.title || '');
  const siteDescription = String(forumAttribute('description', '') || '');
  const siteImage = absoluteUrl(forumAttribute('logoUrl', ''));
  const renderedImage = pageShareImage();

  if (!discussion) {
    const documentTitle = String(document.title || '').trim();
    const titleSuffix = siteTitle ? ` - ${siteTitle}` : '';
    const pageTitle = titleSuffix && documentTitle.endsWith(titleSuffix)
      ? documentTitle.slice(0, -titleSuffix.length).trim()
      : documentTitle;

    return {
      title: shareTitleWithSite(pageTitle, siteTitle),
      description: stripMarkup(siteDescription) || pageTitle || siteTitle,
      link,
      signatureUrl,
      imgUrl: renderedImage || siteImage,
    };
  }

  const title = String(typeof discussion.title === 'function' ? discussion.title() : siteTitle).trim() || siteTitle;
  const firstPost = typeof discussion.firstPost === 'function' ? discussion.firstPost() : null;
  const content = firstPost && typeof firstPost.content === 'function' ? firstPost.content() : '';
  const media = typeof discussion.spacePocketMedia === 'function' ? discussion.spacePocketMedia() : [];
  const firstImage = Array.isArray(media) && media[0] ? media[0].url : '';

  return {
    title: shareTitleWithSite(title, siteTitle),
    description: stripMarkup(content).slice(0, 120) || title,
    link,
    signatureUrl,
    imgUrl: absoluteUrl(firstImage) || renderedImage || siteImage,
  };
};

const loadWechatSdk = () => {
  if (typeof window === 'undefined') return Promise.reject(new Error('Window is unavailable.'));
  if (window.wx) return Promise.resolve(window.wx);
  if (wechatShareState.scriptPromise) return wechatShareState.scriptPromise;

  wechatShareState.scriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = WECHAT_SDK_URL;
    script.async = true;
    script.onload = () => (window.wx ? resolve(window.wx) : reject(new Error('WeChat SDK is unavailable.')));
    script.onerror = () => reject(new Error('Unable to load WeChat SDK.'));
    document.head.appendChild(script);
  });

  return wechatShareState.scriptPromise;
};

const applyWechatShareData = (wx, data) => {
  if (!wx || !data) return;

  const appMessagePayload = {
    title: data.title,
    desc: data.description,
    link: data.link,
    imgUrl: data.imgUrl,
    success: () => {},
  };
  const timelinePayload = {
    title: data.title,
    link: data.link,
    imgUrl: data.imgUrl,
    success: () => {},
  };

  // Keep both API generations for desktop and mobile WeChat compatibility.
  [
    ['onMenuShareAppMessage', appMessagePayload],
    ['onMenuShareTimeline', timelinePayload],
    ['updateAppMessageShareData', appMessagePayload],
    ['updateTimelineShareData', timelinePayload],
  ].forEach(([method, payload]) => {
    try {
      if (typeof wx[method] === 'function') wx[method](payload);
    } catch (error) {
      // An unsupported or invalid share payload must not break other APIs.
    }
  });
};

const configureWechatShare = (data) => {
  if (!data || !isWechatBrowser() || !forumAttribute('spacePocketWechatEnabled', false)) return;

  if (app.current && typeof app.current.get === 'function' && app.current.get('routeName') === 'discussion' && !data.imgUrl) {
    return;
  }

  updateShareMeta(data);

  const signatureUrl = data.signatureUrl || data.link;
  const key = `${data.link}|${signatureUrl}|${data.title}|${data.imgUrl}`;

  if (wechatShareState.lastKey === key) return;

  wechatShareState.lastKey = key;
  wechatShareState.data = data;

  // Flarum is a single-page app. WeChat keeps the authentication URL from
  // the initial webview entry even when Flarum navigates to another topic.
  // Configure the SDK once per full page load, then only update share data.
  if (wechatShareState.configStarted) {
    // The SDK must be configured against the URL that opened this webview.
    // Flarum route changes can update the visible URL, so only update the
    // share payload and never call wx.config or reload the page again.
    if (wechatShareState.ready && wechatShareState.wx) applyWechatShareData(wechatShareState.wx, data);
    return;
  }

  if (wechatShareState.pendingSignatureUrl) return;

  wechatShareState.pendingSignatureUrl = signatureUrl;
  wechatShareState.ready = false;
  wechatShareState.wx = null;
  wechatShareState.signatureUrl = null;
  const requestId = ++wechatShareState.requestId;
  const apiUrl = String(forumAttribute('apiUrl', '/api') || '/api').replace(/\/$/, '');
  const endpoint = `${apiUrl}/space-pocket/wechat/signature?url=${encodeURIComponent(signatureUrl)}&t=${Date.now()}`;

  const request = app && typeof app.request === 'function'
    ? app.request({ method: 'GET', url: endpoint })
    : Promise.reject(new Error('Flarum request API is unavailable.'));

  Promise.all([loadWechatSdk(), request])
    .then(([wx, signature]) => {
      if (requestId !== wechatShareState.requestId || !signature || typeof wx.config !== 'function') return;

      if (typeof wx.error === 'function') {
        wx.error((error) => {
          // Keep the SDK error available in WeChat's remote console when config fails.
          if (typeof console !== 'undefined' && typeof console.error === 'function') {
            console.error('[space-pocket-posts] WeChat SDK error', error);
          }

          const errorMessage = String(error && error.errMsg ? error.errMsg : '').toLowerCase();

          if (errorMessage.includes('config:invalid signature')) {
            // Do not call wx.config again in this page lifecycle. WeChat
            // validates against the initial webview URL, and a second config
            // during SPA navigation is rejected even with a fresh ticket.
            wechatShareState.ready = false;
          }
        });
      }

      wechatShareState.configStarted = true;
      wx.config({
        debug: false,
        appId: signature.appId,
        timestamp: Number(signature.timestamp),
        nonceStr: signature.nonceStr,
        signature: signature.signature,
        jsApiList: WECHAT_SHARE_API_LIST,
      });

      wx.ready(() => {
        if (requestId !== wechatShareState.requestId) return;

        wechatShareState.ready = true;
        wechatShareState.wx = wx;
        wechatShareState.signatureUrl = signatureUrl;
        wechatShareState.pendingSignatureUrl = null;
        applyWechatShareData(wx, wechatShareState.data);
      });

    })
    .catch((error) => {
      // Mobile WeChat can finish loading the page before the SDK or signature
      // request is available. Allow the periodic sync to retry this key.
      if (requestId !== wechatShareState.requestId) return;

      wechatShareState.ready = false;
      wechatShareState.wx = null;
      wechatShareState.configStarted = false;
      wechatShareState.signatureUrl = null;
      wechatShareState.pendingSignatureUrl = null;
      wechatShareState.lastKey = null;

      if (typeof console !== 'undefined' && typeof console.error === 'function') {
        console.error('[space-pocket-posts] WeChat setup failed', error);
      }
    });
};

const installWechatSharing = () => {
  if (!isWechatBrowser() || !DiscussionPage || !DiscussionPage.prototype) return;

  const sync = (discussion = null) => {
    try {
      const current = app && app.current;
      const routeName = current && typeof current.get === 'function' ? current.get('routeName') : '';
      const loadedDiscussion = current && typeof current.get === 'function' ? current.get('discussion') : null;

      if (discussion || loadedDiscussion) wechatShareState.discussion = discussion || loadedDiscussion;

      const currentDiscussion = routeName === 'discussion' ? wechatShareState.discussion : null;
      configureWechatShare(discussionShareData(currentDiscussion));
    } catch (error) {
      // Sharing is optional; keep errors isolated from the Flarum initializer.
    }
  };

  try {
    extend(DiscussionPage.prototype, 'view', function (view) {
      sync(this.discussion);
      return view;
    });
  } catch (error) {
    return;
  }

  sync();
  window.setInterval(() => sync(), 1000);
};

const discussionTopRoute = (discussion) => {
  if (!discussion) return null;

  const routeId =
    (typeof discussion.slug === 'function' && discussion.slug()) ||
    (typeof discussion.id === 'function' && discussion.id());

  return routeId ? app.route('discussion', { id: routeId, near: 1 }) : null;
};

const setLinkHrefByClass = (vnode, targetClass, href) => {
  if (!vnode || !href) return;

  if (Array.isArray(vnode)) {
    vnode.forEach((child) => setLinkHrefByClass(child, targetClass, href));
    return;
  }

  const attrs = vnode.attrs || {};
  const className = attrs.className || attrs.class || '';

  if (typeof className === 'string' && className.split(/\s+/).includes(targetClass)) {
    attrs.href = href;
  }

  setLinkHrefByClass(vnode.children, targetClass, href);
};

const resolveUnknownUserLabels = (node, context = {}) => {
  const normalizedContext = Array.isArray(context) ? { usernames: context } : context || {};
  const queue = Array.isArray(normalizedContext.usernames)
    ? normalizedContext.usernames.map((username) => String(username).trim()).filter(Boolean)
    : [];
  const discussion = normalizedContext.discussion || null;
  let index = 0;

  const nextUsername = () => queue[index++] || null;

  const resolveLabelFromPost = (post) => {
    const user = post && typeof post.user === 'function' ? post.user() : null;

    if (!user) return null;

    if (typeof user.displayName === 'function' && user.displayName()) return user.displayName();
    if (typeof user.username === 'function' && user.username()) return user.username();

    return null;
  };

  const findDiscussionPost = (identifier, byNumber = false) => {
    if (!discussion || identifier === null || identifier === undefined || typeof app.store?.all !== 'function') {
      return null;
    }

    const normalizedIdentifier = String(identifier);
    const posts = app.store.all('posts') || [];

    return posts.find((candidate) => {
      if (!candidate) return false;

      const value = byNumber
        ? typeof candidate.number === 'function' && candidate.number()
        : typeof candidate.id === 'function' && candidate.id();

      if (String(value) !== normalizedIdentifier) return false;

      const candidateDiscussion = typeof candidate.discussion === 'function' ? candidate.discussion() : null;

      return candidateDiscussion && typeof candidateDiscussion.id === 'function' && candidateDiscussion.id() === discussion.id();
    });
  };

  const resolveLabelFromPostId = (postId) => resolveLabelFromPost(findDiscussionPost(postId));

  const resolveLabelFromHref = (href) => {
    if (!discussion || typeof href !== 'string') return null;

    const match = href.match(/\/d\/[^/]+\/(\d+)(?:[/?#].*)?$/);

    if (!match) return null;

    return resolveLabelFromPost(findDiscussionPost(Number(match[1]), true));
  };

  const replaceUnknownUserLabelsInHtml = (html, labelHint = null) => {
    const fallbackLabel = () => labelHint || nextUsername() || null;

    const getDiscussionPostUrl = (post) => {
      if (!post || !discussion || typeof app.route !== 'function') return null;

      const route = app.route('discussion', { id: discussion.id(), near: post.number() });
      const baseUrl = typeof app.forum?.attribute === 'function' ? app.forum.attribute('baseUrl') : '';

      return route ? `${String(baseUrl || '').replace(/\/$/, '')}${route}` : null;
    };

    if (typeof html !== 'string' || !html.includes('<') || typeof DOMParser === 'undefined') {
      return typeof html === 'string' ? html.replace(UNKNOWN_USER_LABEL_PATTERN, (match) => fallbackLabel() || match) : html;
    }

    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    const mentionNodes = documentFragment.body.querySelectorAll('.PostMention--deleted[data-id]');

    mentionNodes.forEach((mentionNode) => {
      const targetPost = findDiscussionPost(mentionNode.getAttribute('data-id'));
      const label = resolveLabelFromPost(targetPost) || fallbackLabel();

      if (!label) return;

      const targetUrl = getDiscussionPostUrl(targetPost);

      if (targetUrl) {
        const replacement = documentFragment.createElement('a');
        replacement.className = 'PostMention';
        replacement.setAttribute('data-id', mentionNode.getAttribute('data-id'));
        replacement.href = targetUrl;
        replacement.textContent = label;
        mentionNode.replaceWith(replacement);
      } else {
        mentionNode.classList.remove('PostMention--deleted');
        mentionNode.textContent = label;
      }
    });

    const textWalker = documentFragment.createTreeWalker(documentFragment.body, 4);
    const textNodes = [];
    let textNode = textWalker.nextNode();

    while (textNode) {
      textNodes.push(textNode);
      textNode = textWalker.nextNode();
    }

    textNodes.forEach((node) => {
      if (node.parentElement && node.parentElement.matches('.PostMention--deleted[data-id]')) return;

      node.nodeValue = node.nodeValue.replace(UNKNOWN_USER_LABEL_PATTERN, (match) => fallbackLabel() || match);
    });

    return documentFragment.body.innerHTML;
  };

  const visit = (current, labelHint = null) => {
    if (Array.isArray(current)) {
      for (let childIndex = 0; childIndex < current.length; childIndex += 1) {
        current[childIndex] = visit(current[childIndex], labelHint);
      }

      return current;
    }

    if (typeof current === 'string') {
      return replaceUnknownUserLabelsInHtml(current, labelHint);
    }

    if (!current || typeof current !== 'object' || !('children' in current)) return current;

    const attrs = current.attrs || {};
    const href = attrs.href || attrs.to || attrs.url;
    const mentionPostId = attrs['data-id'] || attrs.dataId;
    const label = resolveLabelFromPostId(mentionPostId) || resolveLabelFromHref(href) || labelHint;

    current.children = visit(current.children, label);
    return current;
  };

  return visit(node);
};

const getReplyMentionUsernames = (post) => {
  if (!post || typeof post.spacePocketReplyMentionUsers !== 'function') return [];

  const usernames = post.spacePocketReplyMentionUsers();

  return Array.isArray(usernames) ? usernames : [];
};

const getDiscussionReplyMentionUsernames = (discussion) => {
  if (!discussion) return [];

  const relevantPost = typeof discussion.mostRelevantPost === 'function' ? discussion.mostRelevantPost() : null;
  const firstPost = typeof discussion.firstPost === 'function' ? discussion.firstPost() : null;
  const post = relevantPost || firstPost;

  return getReplyMentionUsernames(post);
};

const getPostId = (post) => {
  if (!post || typeof post.id !== 'function') return null;

  const id = post.id();

  return id === null || id === undefined ? null : String(id);
};

const getReplyTargetPostId = (post) => {
  if (!post) return null;

  const contentHtml = post.data && post.data.attributes && post.data.attributes.contentHtml;

  if (typeof contentHtml !== 'string' || !contentHtml || typeof DOMParser === 'undefined') return null;

  const documentFragment = new DOMParser().parseFromString(contentHtml, 'text/html');
  const mention = documentFragment.body.querySelector('.PostMention[data-id]');

  return mention ? mention.getAttribute('data-id') : null;
};

const comparePostsByTime = (left, right) => {
  const leftCreatedAt = left && typeof left.createdAt === 'function' ? left.createdAt() : null;
  const rightCreatedAt = right && typeof right.createdAt === 'function' ? right.createdAt() : null;
  const leftTime = leftCreatedAt instanceof Date ? leftCreatedAt.getTime() : Date.parse(leftCreatedAt || '') || 0;
  const rightTime = rightCreatedAt instanceof Date ? rightCreatedAt.getTime() : Date.parse(rightCreatedAt || '') || 0;

  if (leftTime !== rightTime) return leftTime - rightTime;

  const leftNumber = left && typeof left.number === 'function' ? Number(left.number()) : 0;
  const rightNumber = right && typeof right.number === 'function' ? Number(right.number()) : 0;

  return leftNumber - rightNumber;
};

const flattenPostStreamChildren = (children, result = []) => {
  if (!Array.isArray(children)) return result;

  children.forEach((child) => {
    if (Array.isArray(child)) {
      flattenPostStreamChildren(child, result);
    } else if (child && child.tag === '[') {
      flattenPostStreamChildren(child.children, result);
    } else {
      result.push(child);
    }
  });

  return result;
};

const isCommentStreamItem = (vnode) => {
  if (!vnode || !vnode.attrs || !vnode.attrs['data-id']) return false;

  const className = vnode.attrs.className || vnode.attrs.class || '';

  return typeof className === 'string' && className.split(/\s+/).includes('PostStream-item');
};

const groupPostStreamView = (view, discussion) => {
  if (!view || !discussion || !Array.isArray(view.children)) return view;

  const children = flattenPostStreamChildren(view.children);
  const streamItems = children.filter(isCommentStreamItem);

  if (streamItems.length < 2 || typeof app.store?.all !== 'function') return view;

  const posts = app.store.all('posts') || [];
  const discussionPosts = posts.filter((post) => {
    const postDiscussion = post && typeof post.discussion === 'function' ? post.discussion() : null;

    return postDiscussion && typeof postDiscussion.id === 'function' && postDiscussion.id() === discussion.id();
  });
  const postsById = new Map();

  discussionPosts.forEach((post) => {
    const id = getPostId(post);

    if (id) postsById.set(id, post);
  });

  const firstPostCandidate = typeof discussion.firstPost === 'function' ? discussion.firstPost() : null;
  const firstPost = getPostId(firstPostCandidate)
    ? firstPostCandidate
    : discussionPosts.find((post) => post.number && post.number() === 1);
  const firstPostId = getPostId(firstPost);
  const rootMemo = new Map();
  const resolving = new Set();

  const findRootId = (post) => {
    const postId = getPostId(post);

    if (!postId) return null;
    if (rootMemo.has(postId)) return rootMemo.get(postId);
    if (resolving.has(postId)) return postId;

    resolving.add(postId);
    const targetId = getReplyTargetPostId(post);
    const targetPost = targetId ? postsById.get(String(targetId)) : null;
    let rootId = postId;

    const targetPostId = getPostId(targetPost);

    if (targetPost && (!firstPostId || targetPostId !== firstPostId) && !resolving.has(targetPostId)) {
      rootId = findRootId(targetPost) || postId;
    }

    resolving.delete(postId);
    rootMemo.set(postId, rootId);

    return rootId;
  };

  const visibleItemsById = new Map();
  streamItems.forEach((vnode) => visibleItemsById.set(String(vnode.attrs['data-id']), vnode));

  const groups = new Map();
  streamItems.forEach((vnode) => {
    const postId = String(vnode.attrs['data-id']);
    const post = postsById.get(postId);

    if (!post) return;

    const rootId = findRootId(post);
    const rootIsVisible = rootId && visibleItemsById.has(rootId);

    if (rootId !== postId && rootIsVisible) {
      const group = groups.get(rootId) || { root: visibleItemsById.get(rootId), replies: [] };
      group.replies.push({ post, vnode });
      groups.set(rootId, group);
    } else {
      const group = groups.get(postId) || { root: vnode, replies: [] };
      group.root = vnode;
      groups.set(postId, group);
    }
  });

  const visibleReplyGroups = Array.from(groups.values()).filter((group) => group.replies.length);

  if (!visibleReplyGroups.length) return view;

  visibleReplyGroups.forEach((group) => group.replies.sort((left, right) => comparePostsByTime(left.post, right.post)));

  const renderedRoots = new Set();
  const groupedChildren = [];

  children.forEach((child) => {
    if (!isCommentStreamItem(child)) {
      groupedChildren.push(child);
      return;
    }

    const postId = String(child.attrs['data-id']);
    const group = groups.get(postId);

    if (!group || renderedRoots.has(postId)) return;

    renderedRoots.add(postId);

    if (!group.replies.length) {
      groupedChildren.push(child);
      return;
    }

    groupedChildren.push(
      <ReplyGroup
        key={`space-pocket-reply-group-${postId}`}
        rootId={postId}
        root={group.root}
        replies={group.replies.map((reply) => reply.vnode)}
      />
    );
  });

  view.children = groupedChildren;

  return view;
};

const installReplyGroups = () => {
  if (!PostStream || typeof PostStream.prototype.view !== 'function') return;

  override(PostStream.prototype, 'view', function (original) {
    const view = original();

    return groupPostStreamView(view, this.attrs && this.attrs.discussion);
  });
};

const installMasonryLayout = () => {
  if (app.spacePocketMasonryInstalled) return;

  app.spacePocketMasonryInstalled = true;

  const lists = new Set();
  const observedCards = new WeakSet();
  const cardHeights = new WeakMap();
  const listWidths = new WeakMap();
  const fullLayoutLists = new Set();
  const dirtyCards = new Set();
  let frame = null;
  let resizeObserver = null;

  const scheduleLayout = () => {
    if (frame !== null) return;

    frame = window.requestAnimationFrame(() => {
      frame = null;
      const measurements = [];
      const listStates = [];
      const listMetrics = new Map();
      const pendingFullLayouts = new Set(fullLayoutLists);
      const pendingCards = Array.from(dirtyCards);

      fullLayoutLists.clear();
      dirtyCards.clear();

      lists.forEach((list) => {
        if (!list.isConnected) lists.delete(list);
      });

      const getListMetrics = (list) => {
        if (listMetrics.has(list)) return listMetrics.get(list);

        const columnGap = getComputedStyle(list).columnGap.trim();
        const parsedGap = parseFloat(columnGap) || 0;
        const listWidth = list.getBoundingClientRect().width;
        const metrics = {
          gap: columnGap.endsWith('%') ? (listWidth * parsedGap) / 100 : parsedGap,
          width: listWidth,
        };

        listWidths.set(list, listWidth);
        listMetrics.set(list, metrics);

        return metrics;
      };

      const measureCard = (card, gap) => {
        if (resizeObserver && !observedCards.has(card)) {
          observedCards.add(card);
          resizeObserver.observe(card);
        }

        const previousSpan = card.style.getPropertyValue('--space-pocket-masonry-span');
        const list = card.closest(MASONRY_LIST_SELECTOR);
        const isUnmeasured = !previousSpan && list?.hasAttribute('data-space-pocket-masonry');

        // The CSS fallback span protects the paint between DOM insertion and
        // this frame. Remove it while measuring so it cannot inflate the
        // measured height and become the card's permanent span.
        if (isUnmeasured) card.style.setProperty('--space-pocket-masonry-span', '1');

        // scrollHeight still reflects the card's content when a stale grid
        // span is temporarily smaller than the rendered card box.
        const height = Math.max(card.getBoundingClientRect().height, card.scrollHeight);

        if (isUnmeasured) card.style.removeProperty('--space-pocket-masonry-span');

        cardHeights.set(card, height);
        measurements.push({
          card,
          span: String(Math.max(1, Math.ceil((height + gap) / MASONRY_ROW_HEIGHT))),
        });
      };

      pendingFullLayouts.forEach((list) => {
        if (!list.isConnected) {
          lists.delete(list);
          return;
        }

        const cards = Array.from(list.querySelectorAll(MASONRY_CARD_SELECTOR));
        const { gap } = getListMetrics(list);

        listStates.push({ list, hasCards: cards.length > 0 });
        cards.forEach((card) => measureCard(card, gap));
      });

      pendingCards.forEach((card) => {
        if (!card.isConnected) return;

        const list = card.closest(MASONRY_LIST_SELECTOR);

        if (!(list instanceof HTMLElement) || pendingFullLayouts.has(list)) return;

        if (!lists.has(list)) {
          lists.add(list);
          if (resizeObserver) resizeObserver.observe(list);
        }

        measureCard(card, getListMetrics(list).gap);
      });

      measurements.forEach(({ card, span }) => {
        if (card.style.getPropertyValue('--space-pocket-masonry-span') !== span) {
          card.style.setProperty('--space-pocket-masonry-span', span);
        }
      });

      listStates.forEach(({ list, hasCards }) => {
        if (hasCards) {
          const rowHeight = `${MASONRY_ROW_HEIGHT}px`;

          if (list.style.getPropertyValue('grid-auto-rows') !== rowHeight) {
            list.style.setProperty('grid-auto-rows', rowHeight);
          }

          if (!list.hasAttribute('data-space-pocket-masonry')) {
            list.setAttribute('data-space-pocket-masonry', '');
          }
        } else {
          list.style.removeProperty('grid-auto-rows');
          list.removeAttribute('data-space-pocket-masonry');
        }
      });
    });
  };

  const registerList = (list) => {
    if (!(list instanceof HTMLElement)) return;

    if (!lists.has(list)) {
      lists.add(list);
      if (resizeObserver) resizeObserver.observe(list);
    }

    fullLayoutLists.add(list);
    scheduleLayout();
  };

  const discoverLists = (root) => {
    if (!(root instanceof Element) && root !== document) return;

    if (root instanceof Element && root.matches(MASONRY_LIST_SELECTOR)) registerList(root);
    root.querySelectorAll(MASONRY_LIST_SELECTOR).forEach(registerList);

    if (root instanceof Element) registerList(root.closest(MASONRY_LIST_SELECTOR));
  };

  if (typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver((entries) => {
      let shouldLayout = false;

      entries.forEach((entry) => {
        const list = entry.target.matches(MASONRY_LIST_SELECTOR)
          ? entry.target
          : entry.target.closest(MASONRY_LIST_SELECTOR);

        if (!(list instanceof HTMLElement)) return;

        if (entry.target === list) {
          const previousWidth = listWidths.get(list);

          if (previousWidth !== undefined && Math.abs(previousWidth - entry.contentRect.width) < 0.5) return;

          fullLayoutLists.add(list);
        } else {
          const borderBox = Array.isArray(entry.borderBoxSize) ? entry.borderBoxSize[0] : entry.borderBoxSize;
          const height = borderBox ? borderBox.blockSize : entry.target.getBoundingClientRect().height;
          const previousHeight = cardHeights.get(entry.target);

          if (previousHeight !== undefined && Math.abs(previousHeight - height) < 0.5) return;

          dirtyCards.add(entry.target);
        }

        lists.add(list);
        shouldLayout = true;
      });

      if (shouldLayout) scheduleLayout();
    });
  }

  const mutationObserver = new MutationObserver((records) => {
    records.forEach((record) => {
      discoverLists(record.target);
      record.addedNodes.forEach(discoverLists);
    });
  });

  mutationObserver.observe(document.body, { childList: true, subtree: true });
  window.addEventListener('resize', () => {
    lists.forEach((list) => fullLayoutLists.add(list));
    scheduleLayout();
  });
  window.addEventListener(
    'load',
    (event) => {
      const image = event.target;

      if (!(image instanceof HTMLImageElement)) return;

      const card = image.closest('.CardsListItem');

      if (!card) return;

      dirtyCards.add(card);
      scheduleLayout();
    },
    true
  );
  discoverLists(document);
};

const installInfiniteDiscussionScroll = () => {
  if (app.spacePocketInfiniteScrollInstalled || typeof IntersectionObserver === 'undefined') return;

  app.spacePocketInfiniteScrollInstalled = true;

  const observed = new WeakSet();
  const loading = new WeakSet();
  const preloadMargin = window.matchMedia('(min-width: 992px)').matches
    ? Math.max(DESKTOP_AUTO_LOAD_MIN_MARGIN, Math.round(window.innerHeight * DESKTOP_AUTO_LOAD_VIEWPORTS))
    : AUTO_LOAD_MARGIN;

  const loadIfNear = (loadMore) => {
    if (!(loadMore instanceof HTMLElement)) return;
    if (app.spacePocketTopicRefreshPending) return;

    const button = loadMore.querySelector('button');

    if (!button || button.disabled || loading.has(loadMore)) return;

    button.tabIndex = -1;
    button.setAttribute('aria-hidden', 'true');

    const rect = loadMore.getBoundingClientRect();

    if (rect.top > window.innerHeight + preloadMargin || rect.bottom < -preloadMargin) return;

    const discussionList = loadMore.parentElement;
    const initialCardCount = discussionList?.querySelectorAll('.CardsListItem').length || 0;
    const startedAt = Date.now();
    let sawDisabledButton = false;

    loading.add(loadMore);
    button.click();

    const continueWhenReady = () => {
      if (!loadMore.isConnected) {
        loading.delete(loadMore);
        return;
      }

      const currentButton = loadMore.querySelector('button');
      const currentCardCount = discussionList?.querySelectorAll('.CardsListItem').length || 0;

      if (currentButton?.disabled) sawDisabledButton = true;

      const requestFinished =
        !currentButton ||
        currentCardCount !== initialCardCount ||
        (sawDisabledButton && !currentButton.disabled);
      const requestTimedOut = Date.now() - startedAt >= AUTO_LOAD_STATUS_TIMEOUT;

      if (requestFinished || requestTimedOut) {
        loading.delete(loadMore);
        loadIfNear(loadMore);
        return;
      }

      window.setTimeout(continueWhenReady, AUTO_LOAD_STATUS_POLL_INTERVAL);
    };

    window.setTimeout(continueWhenReady, AUTO_LOAD_STATUS_POLL_INTERVAL);
  };

  const intersectionObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) loadIfNear(entry.target);
      });
    },
    { rootMargin: `${preloadMargin}px 0px` }
  );

  const registerLoadMore = (loadMore) => {
    if (!(loadMore instanceof HTMLElement)) return;

    loadMore.parentElement?.setAttribute('data-space-pocket-infinite-scroll', '');

    if (!observed.has(loadMore)) {
      observed.add(loadMore);
      intersectionObserver.observe(loadMore);
    }

    loadIfNear(loadMore);
  };

  const discoverLoadMore = (root) => {
    if (!(root instanceof Element) && root !== document) return;

    if (root instanceof Element && root.matches(AUTO_LOAD_MORE_SELECTOR)) registerLoadMore(root);
    root.querySelectorAll(AUTO_LOAD_MORE_SELECTOR).forEach(registerLoadMore);

    if (root instanceof Element) registerLoadMore(root.closest(AUTO_LOAD_MORE_SELECTOR));
  };

  const mutationObserver = new MutationObserver((records) => {
    records.forEach((record) => {
      discoverLoadMore(record.target);
      record.addedNodes.forEach(discoverLoadMore);
    });
  });

  mutationObserver.observe(document.body, { childList: true, subtree: true });
  discoverLoadMore(document);
};

class LegacyUserDiscussionsPage extends Component {
  oncreate(vnode) {
    super.oncreate(vnode);
    m.route.set(app.route('user.discussions', { username: m.route.param('username') }), null, { replace: true });
  }

  view() {
    return <LoadingIndicator />;
  }
}

const imageRequiredMessage = () => {
  const translated = app.translator.trans(IMAGE_REQUIRED_TRANSLATION_KEY);

  if (String(translated) !== IMAGE_REQUIRED_TRANSLATION_KEY) return translated;

  const locale = (document.documentElement.lang || '').toLowerCase();

  return locale.startsWith('zh') ? '请添加图片后再发布' : 'Add at least one image before publishing';
};

const dismissImageRequiredAlert = () => {
  if (!app.composer.spacePocketImageRequiredAlert) return;

  app.alerts.dismiss(app.composer.spacePocketImageRequiredAlert);
  app.composer.spacePocketImageRequiredAlert = null;
};

const installComposerAlertCleanup = () => {
  if (app.composer.spacePocketAlertCleanupInstalled) return;

  app.composer.spacePocketAlertCleanupInstalled = true;
  document.addEventListener('click', (event) => {
    if (event.target.closest && event.target.closest('.Composer .item-minimize button')) {
      dismissImageRequiredAlert();
    }
  });
};

const installDraftsTooltipCleanup = () => {
  if (app.spacePocketDraftsTooltipCleanupInstalled) return;

  app.spacePocketDraftsTooltipCleanupInstalled = true;
  document.addEventListener('click', (event) => {
    const button =
      event.target.closest && event.target.closest('.SpacePocketPosts-bottomDrafts .App-primaryControl .Alert-dismiss');

    if (!button) return;

    const tooltipId = button.getAttribute('aria-describedby');

    if (!tooltipId) return;

    window.setTimeout(() => {
      document.getElementById(tooltipId)?.remove();
      button.removeAttribute('aria-describedby');
    }, 0);
  });
};

const installMobileBottomNav = () => {
  if (document.getElementById('space-pocket-posts-bottom-nav')) return;

  const root = document.createElement('div');
  root.id = 'space-pocket-posts-bottom-nav';
  document.body.appendChild(root);
  window.setTimeout(() => {
    if (root.isConnected) m.mount(root, MobileBottomNav);
  }, 0);
};

const installMobileNavScrollLock = () => {
  if (app.spacePocketMobileNavScrollLockInstalled) return;

  app.spacePocketMobileNavScrollLockInstalled = true;
  const mobileViewport = window.matchMedia('(max-width: 767.98px)');
  let lockedScrollY = 0;
  let locked = false;
  let syncFrame = null;

  const openDropdown = () =>
    document.querySelector('.IndexPage-nav .Dropdown.open, .SpacePocketPosts-bottomNav .Dropdown.open');

  const unlock = () => {
    if (!locked) return;

    locked = false;
    document.documentElement.classList.remove('SpacePocketPosts-mobileNavOpen');
    document.documentElement.classList.remove('SpacePocketPosts-mobileIndexNavOpen');
    document.body.style.removeProperty('--space-pocket-mobile-nav-scroll-y');
    window.scrollTo(0, lockedScrollY);
  };

  const sync = () => {
    syncFrame = null;
    const dropdown = mobileViewport.matches ? openDropdown() : null;
    const shouldLock = Boolean(dropdown);

    document.documentElement.classList.toggle(
      'SpacePocketPosts-mobileIndexNavOpen',
      Boolean(dropdown?.closest('.IndexPage-nav'))
    );

    if (!shouldLock) {
      unlock();
      return;
    }

    if (locked) return;

    locked = true;
    lockedScrollY = window.scrollY;
    document.body.style.setProperty('--space-pocket-mobile-nav-scroll-y', `${lockedScrollY}px`);
    document.documentElement.classList.add('SpacePocketPosts-mobileNavOpen');
  };

  const scheduleSync = () => {
    if (syncFrame !== null) return;
    syncFrame = window.requestAnimationFrame(sync);
  };

  new MutationObserver(scheduleSync).observe(document.body, {
    attributes: true,
    attributeFilter: ['class'],
    childList: true,
    subtree: true,
  });
  window.addEventListener('resize', scheduleSync);
  scheduleSync();
};

const installIndexTranslationOverrides = () => {
  if (app.spacePocketIndexTranslationOverridesInstalled) return;

  app.spacePocketIndexTranslationOverridesInstalled = true;

  const originalTranslate = app.translator.trans.bind(app.translator);
  const normalize = (value) => (Array.isArray(value) ? value : [value]);

  app.translator.trans = function (...args) {
    const [key, parameters, ...rest] = args;

    if (key === INDEX_NEW_DISCUSSION_TRANSLATION_KEY) {
      return normalize('发布帖子');
    }

    if (key === INDEX_DISCUSSIONS_LINK_TRANSLATION_KEY) {
      return normalize(app.session.user ? '为你推荐' : '浏览发现');
    }

    if (key === INDEX_TOP_SORT_TRANSLATION_KEY) {
      return normalize('回复最多');
    }

    return originalTranslate(key, parameters, ...rest);
  };
};

const normalizeTopicId = (value) => {
  const id = String(value || '').trim();

  return /^\d+$/.test(id) && Number(id) > 0 ? id : null;
};

const currentIndexTopicId = () => {
  // During a discussion -> index transition, the browser URL is updated before
  // the Mithril route state used by the first list request.
  if (typeof window !== 'undefined' && window.location) {
    try {
      const browserUrl = new URL(window.location.href);

      if (browserUrl.pathname !== '/' && browserUrl.pathname !== '') return null;

      const browserTopicId = normalizeTopicId(browserUrl.searchParams.get('topic'));

      if (browserTopicId) return browserTopicId;
    } catch (error) {
      // Fall back to Mithril's route state below.
    }
  }

  const route = typeof m.route.get === 'function' ? String(m.route.get() || '') : '';
  const path = route.split(/[?#]/, 1)[0] || '/';

  if (path !== '/') return null;

  return normalizeTopicId(m.route.param('topic'));
};

const loadPopularTopics = () => {
  if (!app.forum.attribute('spacePocketTopicsEnabled')) return Promise.resolve([]);

  const cacheIsFresh =
    popularTopicsCache.loadedAt > 0 && Date.now() - popularTopicsCache.loadedAt < POPULAR_TOPICS_CACHE_TTL;

  if (cacheIsFresh) return Promise.resolve(popularTopicsCache.topics);
  if (popularTopicsCache.request) return popularTopicsCache.request;

  popularTopicsCache.request = app
    .request({
      method: 'GET',
      url: app.forum.attribute('apiUrl') + '/space-pocket/topics/popular',
    })
    .then((response) => {
      popularTopicsCache.topics = (Array.isArray(response.data) ? response.data : [])
        .map((resource) => ({
          id: normalizeTopicId(resource.id),
          name: String(resource.attributes?.name || '').trim(),
        }))
        .filter((topic) => topic.id && topic.name);
      popularTopicsCache.loadedAt = Date.now();

      return popularTopicsCache.topics;
    })
    .catch(() => {
      popularTopicsCache.topics = [];
      popularTopicsCache.loadedAt = Date.now();

      return [];
    })
    .finally(() => {
      popularTopicsCache.request = null;
    });

  return popularTopicsCache.request;
};

const topicIndexRoute = (topicId, sort = m.route.param('sort'), topicName = null) => {
  const params = { topic: topicId };
  const normalizedTopicName = String(topicName || '').trim();

  if (sort && sort !== 'latest') params.sort = sort;
  if (normalizedTopicName) params.topicName = normalizedTopicName;

  return app.route('index', params);
};

const refreshIndexTopicDiscussions = () => {
  const topicId = currentIndexTopicId();
  const state = app.discussions;

  if (!topicId || !state || typeof state.refresh !== 'function') return;

  const topicChanged = state.spacePocketTopicId !== topicId;
  state.spacePocketTopicId = topicId;

  if (!topicChanged) return;

  app.spacePocketTopicRefreshPending = true;
  const refreshToken = (app.spacePocketTopicRefreshToken || 0) + 1;
  app.spacePocketTopicRefreshToken = refreshToken;
  const releaseWhenReady = () => {
    if (app.spacePocketTopicRefreshToken !== refreshToken) return;

    if (state.initialLoading || state.loadingNext || state.loadingPrev) {
      window.setTimeout(releaseWhenReady, AUTO_LOAD_STATUS_POLL_INTERVAL);
      return;
    }

    app.spacePocketTopicRefreshPending = false;
    m.redraw();
  };

  // IndexPage's own oninit may already be refreshing the shared state. In
  // that case wait for its request instead of starting a competing refresh.
  if (state.initialLoading) {
    releaseWhenReady();
    return;
  }

  Promise.resolve(state.refresh()).finally(releaseWhenReady);
};

const installPopularTopicNavigation = () => {
  extend(IndexPage.prototype, 'oninit', function () {
    // Flarum may preload the unfiltered first page on a hard navigation, and
    // it keeps the shared list state on a discussion-to-index transition.
    // Marking the active topic and refreshing here handles both cases.
    refreshIndexTopicDiscussions();
    loadPopularTopics().then(() => m.redraw());
  });

  extend(IndexPage.prototype, 'navItems', function (items) {
    items.remove('tags');

    if (!app.forum.attribute('spacePocketTopicsEnabled')) return;

    const currentTopicId = currentIndexTopicId();
    const currentTopicName = String(m.route.param('topicName') || '').trim();
    const currentTopicIsPopular = popularTopicsCache.topics.some((topic) => topic.id === currentTopicId);
    const allDiscussions = items.get('allDiscussions');

    if (currentTopicId && allDiscussions?.attrs) allDiscussions.attrs.active = false;

    if (!popularTopicsCache.topics.length && !currentTopicName) return;

    items.add(
      'space-pocket-popular-topics-separator',
      <span className="SpacePocketPosts-popularTopicsSeparator" aria-hidden="true" />,
      -20
    );

    if (currentTopicId && currentTopicName && !currentTopicIsPopular) {
      items.add(
        `space-pocket-current-topic-${currentTopicId}`,
        <LinkButton
          className="SpacePocketPosts-popularTopicLink"
          icon="fas fa-hashtag"
          href={topicIndexRoute(currentTopicId, m.route.param('sort'), currentTopicName)}
          active={true}
        >
          {currentTopicName}
        </LinkButton>,
        -21 - popularTopicsCache.topics.length - 1
      );
    }

    popularTopicsCache.topics.forEach((topic, index) => {
      items.add(
        `space-pocket-topic-${topic.id}`,
        <LinkButton
          className="SpacePocketPosts-popularTopicLink"
          icon="fas fa-hashtag"
          href={topicIndexRoute(topic.id, m.route.param('sort'), topic.name)}
          active={currentTopicId === topic.id}
        >
          {topic.name}
        </LinkButton>,
        -21 - index
      );
    });
  });

  extend(DiscussionListState.prototype, 'requestParams', function (params) {
    const topicId = currentIndexTopicId();

    if (!topicId) return;

    params.filter = {
      ...(params.filter || {}),
      spacePocketTopic: topicId,
    };
  });

  override(DiscussionListState.prototype, 'loadPage', function (original, page) {
    // The server preloads the unfiltered index page. Consume that document
    // without using it when a topic filter is active; otherwise it becomes a
    // stale first page before the topic-filtered request starts.
    if (currentIndexTopicId() && typeof app.preloadedApiDocument === 'function') {
      app.preloadedApiDocument();
    }

    return original(page);
  });

  extend(IndexPage.prototype, 'viewItems', function (items) {
    const topicId = currentIndexTopicId();

    if (!topicId) return;

    const selectedSort = String(m.route.param('sort') || 'latest');
    const sortOptions = Object.keys(app.discussions.sortMap());

    items.setContent(
      'sort',
      <SelectDropdown
        buttonClassName="Button"
        accessibleToggleLabel={app.translator.trans('space-pocket-posts.forum.topics.sort')}
      >
        {sortOptions.map((sort) => (
          <Button
            icon={sort === selectedSort ? 'fas fa-check' : true}
            active={sort === selectedSort}
            onclick={(event) => {
              event.preventDefault();
              m.route.set(topicIndexRoute(topicId, sort, m.route.param('topicName')));
            }}
          >
            {app.translator.trans(`core.forum.index_sort.${sort}_button`)}
          </Button>
        ))}
      </SelectDropdown>
    );
  });
};

const canComposeTopics = () => {
  if (!app.forum.attribute('spacePocketTopicsEnabled') || !app.composer.body) return false;

  const body = app.composer.body;

  return (
    body.componentClass === DiscussionComposer ||
    (body.componentClass === EditPostComposer && body.attrs.post && body.attrs.post.number() === 1)
  );
};

const textBeforeCaret = (target) => {
  if (target instanceof HTMLTextAreaElement) {
    return target.value.slice(0, target.selectionStart == null ? target.value.length : target.selectionStart);
  }

  const editor = target.closest && target.closest('[contenteditable="true"]');
  const selection = window.getSelection();

  if (!editor || !selection || !selection.rangeCount || !editor.contains(selection.anchorNode)) return null;

  try {
    const range = selection.getRangeAt(0).cloneRange();
    range.selectNodeContents(editor);
    range.setEnd(selection.anchorNode, selection.anchorOffset);
    return range.toString();
  } catch (error) {
    return null;
  }
};

const installTopicShortcut = () => {
  if (app.spacePocketTopicShortcutInstalled) return;

  app.spacePocketTopicShortcutInstalled = true;
  document.addEventListener('keydown', (event) => {
    if (
      event.key !== '#' ||
      event.defaultPrevented ||
      event.repeat ||
      event.isComposing ||
      event.ctrlKey ||
      event.metaKey ||
      event.altKey ||
      !canComposeTopics() ||
      !event.target.closest ||
      !event.target.closest('.TextEditor-editorContainer')
    ) {
      return;
    }

    const beforeCaret = textBeforeCaret(event.target);

    if (beforeCaret === null || (beforeCaret !== '' && !/\s$/.test(beforeCaret))) return;

    event.preventDefault();
    document.dispatchEvent(new CustomEvent(TOPIC_SHORTCUT_EVENT));
  });
};

const installReadablePostMentions = () => {
  if (!app.mentionFormats || typeof app.mentionFormats.mentionable !== 'function') return;

  const postMention = app.mentionFormats.mentionable('post');

  if (!postMention || typeof postMention.replacement !== 'function' || postMention.spacePocketReadableMentions) return;

  const originalReplacement = postMention.replacement;
  postMention.spacePocketReadableMentions = true;
  postMention.replacement = function (post) {
    const syntax = originalReplacement.call(this, post);

    if (!app.composer.body || app.composer.body.componentClass !== ReplyComposer) return syntax;

    const match = syntax.match(POST_MENTION_PATTERN);

    if (!match) return syntax;

    const visible = `@${match[1]}`;
    const mentions = app.composer.spacePocketPostMentions || (app.composer.spacePocketPostMentions = []);
    mentions.push({ syntax, visible });

    return visible;
  };
};

const restorePostMentionIds = (content, mentions) => {
  if (typeof content !== 'string' || !Array.isArray(mentions)) return content;

  return mentions.reduce((value, mention) => {
    const index = value.indexOf(mention.visible);

    if (index === -1) return value;

    return value.slice(0, index) + mention.syntax + value.slice(index + mention.visible.length);
  }, content);
};

const extendDiscussionCardsCover = () => {
  const DiscussionMedia = flarum.core.compat['walsgit/discussion/cards/components/common/DiscussionMedia'];

  if (!DiscussionMedia) return;

  extend(DiscussionMedia.prototype, 'view', function (view) {
    const discussion = this.attrs.discussion;
    const media = cloneMedia(discussion && discussion.spacePocketMedia());

    if (!media.length || !view || !Array.isArray(view.children)) return;

    const imageIndex = view.children.findIndex((child) => {
      if (!child || !child.attrs) return false;

      const className = child.attrs.className || child.attrs.class || '';

      return className === 'previewCardImg' || className === 'imgStub';
    });

    if (imageIndex === -1) return;

    const sourceAttrs = view.children[imageIndex].attrs || {};
    const originalAttrs = {};

    ['width', 'height', 'style', 'role', 'aria-hidden'].forEach((name) => {
      if (sourceAttrs[name] !== undefined) originalAttrs[name] = sourceAttrs[name];
    });

    view.children[imageIndex] = m('img', {
      ...originalAttrs,
      src: media[0].url,
      className: 'previewCardImg',
      alt: discussion.title(),
      loading: 'lazy',
      decoding: 'async',
    });
  });
};

const discussionFromVNode = (vnode) => {
  if (!vnode) return null;
  if (Array.isArray(vnode)) {
    for (const child of vnode) {
      const discussion = discussionFromVNode(child);

      if (discussion) return discussion;
    }

    return null;
  }

  if (vnode.attrs && vnode.attrs.discussion) return vnode.attrs.discussion;

  return discussionFromVNode(vnode.children);
};

const discussionsFromVNode = (vnode, discussions = []) => {
  if (!vnode) return discussions;

  if (Array.isArray(vnode)) {
    vnode.forEach((child) => discussionsFromVNode(child, discussions));
    return discussions;
  }

  if (vnode.attrs && vnode.attrs.discussion) discussions.push(vnode.attrs.discussion);

  discussionsFromVNode(vnode.children, discussions);

  return discussions;
};

const replaceDiscussionListItems = (vnode, CardItem) => {
  if (Array.isArray(vnode)) {
    return vnode.map((child) => replaceDiscussionListItems(child, CardItem));
  }

  if (vnode && vnode.tag === '[') {
    vnode.children = replaceDiscussionListItems(vnode.children || [], CardItem);
    return vnode;
  }

  if (vnode && vnode.tag === CardItem) return vnode;

  if (vnode && vnode.attrs && vnode.attrs.discussion) {
    const discussion = vnode.attrs.discussion;

    return <CardItem key={discussion.id()} discussion={discussion} />;
  }

  if (!vnode || vnode.tag !== 'li') return vnode;

  const discussion = discussionFromVNode(vnode);

  return discussion ? <CardItem key={discussion.id()} discussion={discussion} /> : vnode;
};

const isDiscussionListVNode = (vnode) => {
  if (!vnode || (vnode.tag !== 'ul' && vnode.tag !== 'div')) return false;

  const className = vnode.attrs && (vnode.attrs.className || vnode.attrs.class);

  return typeof className === 'string' && className.split(/\s+/).includes('DiscussionList-discussions');
};

const mergeDiscussionListVNodes = (vnode) => {
  if (!vnode || vnode.tag !== 'div' || !Array.isArray(vnode.children)) return;

  const className = vnode.attrs && (vnode.attrs.className || vnode.attrs.class);

  if (typeof className !== 'string' || !className.split(/\s+/).includes('DiscussionList')) return;

  const lists = vnode.children.filter(isDiscussionListVNode);

  if (lists.length < 2) return;

  const firstList = lists[0];
  const firstChildren = Array.isArray(firstList.children)
    ? firstList.children
    : firstList.children === undefined || firstList.children === null
      ? []
      : [firstList.children];

  firstList.children = lists.slice(1).reduce((children, list) => {
    if (Array.isArray(list.children)) return children.concat(list.children);
    if (list.children === undefined || list.children === null) return children;

    return children.concat([list.children]);
  }, firstChildren);

  vnode.children = vnode.children.filter((child) => child === firstList || !lists.includes(child));
};

const transformDiscussionListsToCards = (vnode, CardItem) => {
  if (!vnode) return;

  if (Array.isArray(vnode)) {
    vnode.forEach((child) => transformDiscussionListsToCards(child, CardItem));
    return;
  }

  const className = vnode.attrs && (vnode.attrs.className || vnode.attrs.class);

  mergeDiscussionListVNodes(vnode);

  if (
    (vnode.tag === 'ul' || vnode.tag === 'div') &&
    typeof className === 'string' &&
    className.split(/\s+/).includes('DiscussionList-discussions')
  ) {
    const classAttribute = vnode.attrs.className !== undefined ? 'className' : 'class';
    const classes = new Set(className.split(/\s+/).filter(Boolean));
    classes.add('flexCard');
    classes.add('SpacePocketPosts-userDiscussionCards');
    vnode.attrs[classAttribute] = Array.from(classes).join(' ');
    vnode.children = replaceDiscussionListItems(vnode.children || [], CardItem);
    return;
  }

  transformDiscussionListsToCards(vnode.children, CardItem);
};

const extendUserDiscussionCards = () => {
  const compat = flarum.core.compat['walsgit/discussion/cards/components/CardItem'];
  const CardItem = compat && (compat.default || compat);

  if (!CardItem) return;

  override(CardItem.prototype, 'view', function (original) {
    const view = original();
    setLinkHrefByClass(view, 'cardLink', discussionTopRoute(this.attrs.discussion));
    resolveUnknownUserLabels(view, {
      discussion: this.attrs.discussion,
      usernames: getDiscussionReplyMentionUsernames(this.attrs.discussion),
    });
    return view;
  });

  if (DiscussionsSearchItem && typeof DiscussionsSearchItem.prototype.view === 'function') {
    override(DiscussionsSearchItem.prototype, 'view', function (original) {
      const view = original();
      const post =
        this.attrs.mostRelevantPost ||
        (this.attrs.discussion && typeof this.attrs.discussion.mostRelevantPost === 'function'
          ? this.attrs.discussion.mostRelevantPost()
          : null);

      resolveUnknownUserLabels(view, {
        discussion: this.attrs.discussion || (post && typeof post.discussion === 'function' ? post.discussion() : null),
        usernames: getReplyMentionUsernames(post),
      });
      return view;
    });
  }

  override(DiscussionList.prototype, 'view', function (original) {
    const view = original();
    const routeName = app.current.get('routeName');
    const cardsEnabledOnIndex = Number(app.forum.attribute('walsgitDiscussionCardsOnIndexPage')) === 1;

    if (routeName === 'user.discussions' || (routeName === 'index' && cardsEnabledOnIndex)) {
      transformDiscussionListsToCards(view, CardItem);
    }

    return view;
  });
};

const installDiscussionNavigation = () => {
  override(DiscussionListItem.prototype, 'mainView', function (original) {
    const view = original();

    if (!this.attrs.params || !this.attrs.params.q) {
      setLinkHrefByClass(view, 'DiscussionListItem-main', discussionTopRoute(this.attrs.discussion));
    }

    return view;
  });

  override(DiscussionList.prototype, 'view', function (original) {
    const view = original();

    registerDiscussionList(discussionsFromVNode(view));

    return view;
  });
};

const configureUserRoutes = () => {
  const userRoute = app.routes.user;
  const postsRoute = app.routes['user.posts'];
  const discussionsRoute = app.routes['user.discussions'];

  if (!userRoute || !postsRoute || !discussionsRoute) return;

  app.routes.user = { ...userRoute, component: discussionsRoute.component };
  app.routes['user.posts'] = { ...postsRoute, path: '/u/:username/posts' };
  app.routes['user.discussions'] = { ...discussionsRoute, path: '/u/:username' };
  app.routes.spacePocketLegacyUserDiscussions = {
    path: '/u/:username/discussions',
    component: LegacyUserDiscussionsPage,
  };
};

app.initializers.add('space-pocket-posts', () => {
  app.routes.spacePocketSearch = { path: '/search', component: MobileSearchPage };
  installIndexTranslationOverrides();
  installPopularTopicNavigation();
  configureUserRoutes();
  Discussion.prototype.spacePocketMedia = Model.attribute('spacePocketMedia');
  Discussion.prototype.spacePocketTopics = Model.attribute('spacePocketTopics');
  Discussion.prototype.spacePocketTopicLinks = Model.attribute('spacePocketTopicLinks');
  Post.prototype.spacePocketReplyMentionUsers = Model.attribute('spacePocketReplyMentionUsers');
  Search.MIN_SEARCH_LEN = 2;
  extend(Search.prototype, 'sourceItems', (items) => {
    if (items.has('discussions')) items.setContent('discussions', new FuzzyDiscussionsSearchSource());
  });
  extendDiscussionCardsCover();
  installDiscussionNavigation();
  extendUserDiscussionCards();
  installReplyGroups();
  installMasonryLayout();
  installInfiniteDiscussionScroll();
  installWechatSharing();
  installReadablePostMentions();
  installTopicShortcut();
  installMobileBottomNav();
  installMobileNavScrollLock();
  installComposerAlertCleanup();
  installDraftsTooltipCleanup();

  // The list's delete control hides the discussion. Make deleting the first
  // post from its detail page use the same discussion-level action.
  override(PostControls, 'hideAction', function (original) {
    const post = this;

    if (!post || typeof post.number !== 'function' || post.number() !== 1) {
      return original();
    }

    const discussion = typeof post.discussion === 'function' ? post.discussion() : null;

    if (!discussion || typeof discussion.canHide !== 'function' || !discussion.canHide()) {
      return original();
    }

    const result = DiscussionControls.hideAction.call(discussion);

    if (!result || typeof result.then !== 'function') return result;

    return result.then(() => {
      if (app.viewingDiscussion(discussion)) app.history.back();
    });
  });

  // A discussion can be hidden while its first post remains visible. In that
  // state, expose the discussion's restore and permanent-delete controls in
  // the first post menu instead of offering another hide action.
  override(PostControls, 'destructiveControls', function (original, post, context) {
    if (!post || typeof post.number !== 'function' || post.number() !== 1) {
      return original(post, context);
    }

    const discussion = typeof post.discussion === 'function' ? post.discussion() : null;

    if (!discussion || typeof discussion.isHidden !== 'function' || !discussion.isHidden()) {
      return original(post, context);
    }

    return DiscussionControls.destructiveControls(discussion);
  });

  extend(DiscussionComposer.prototype, 'oninit', function () {
    this.spacePocketMedia = cloneMedia(app.spacePocketForumProductShareMedia);
    app.spacePocketForumProductShareMedia = null;
    dismissImageRequiredAlert();
    this.composer.spacePocketTopics = [];
    this.composer.spacePocketTopicsDiscussion = null;
    this.composer.spacePocketOriginalTopics = '[]';
    this.composer.spacePocketTopicsSubmitted = false;
  });

  extend(DiscussionComposer.prototype, 'onremove', function () {
    dismissImageRequiredAlert();
  });

  extend(DiscussionComposer.prototype, 'headerItems', function (items) {
    items.add('space-pocket-media', <MediaComposer composer={this} />, -20);
  });

  extend(DiscussionComposer.prototype, 'data', function (data) {
    data.spacePocketMedia = cloneMedia(this.spacePocketMedia);
    data.spacePocketTopics = cloneTopics(this.composer.spacePocketTopics);

    if (this.spacePocketMedia.length && (!data.content || !String(data.content).trim())) {
      data.content = IMAGE_ONLY_CONTENT;
    }
  });

  override(DiscussionComposer.prototype, 'hasChanges', function (original) {
    return (
      original() ||
      Boolean(this.spacePocketMedia && this.spacePocketMedia.length) ||
      Boolean(this.composer.spacePocketTopics && this.composer.spacePocketTopics.length)
    );
  });

  override(DiscussionComposer.prototype, 'onsubmit', function (original) {
    if (this.spacePocketMediaUploading) {
      app.alerts.show({ type: 'error' }, app.translator.trans('space-pocket-posts.forum.composer.wait_upload'));
      return;
    }

    if (!this.spacePocketMedia || !this.spacePocketMedia.length) {
      dismissImageRequiredAlert();
      app.composer.spacePocketImageRequiredAlert = app.alerts.show({ type: 'error' }, imageRequiredMessage());
      return;
    }

    dismissImageRequiredAlert();
    return original();
  });

  extend(ReplyComposer.prototype, 'oninit', function () {
    app.composer.spacePocketPostMentions = [];
  });

  extend(ReplyComposer.prototype, 'data', function (data) {
    data.content = restorePostMentionIds(data.content, this.composer.spacePocketPostMentions);
  });

  extend(TextEditor.prototype, 'controlItems', function (items) {
    if (canComposeTopics()) {
      items.add('space-pocket-topics', <TopicComposer composer={this.attrs.composer} />, -20);
    }
  });

  extend(EditPostComposer.prototype, 'oninit', function () {
    const post = this.attrs.post;

    if (post && post.number() === 1) {
      const discussion = post.discussion();
      this.spacePocketMedia = cloneMedia(discussion && discussion.spacePocketMedia());
      this.spacePocketOriginalMedia = JSON.stringify(this.spacePocketMedia);
      this.spacePocketMediaDiscussion = discussion;
      this.composer.spacePocketTopics = cloneTopics(discussion && discussion.spacePocketTopics());
      this.spacePocketOriginalTopics = JSON.stringify(this.composer.spacePocketTopics);
      this.composer.spacePocketTopicsDiscussion = discussion;
      this.composer.spacePocketOriginalTopics = this.spacePocketOriginalTopics;
      this.composer.spacePocketTopicsSubmitted = false;
    }
  });

  extend(EditPostComposer.prototype, 'headerItems', function (items) {
    const post = this.attrs.post;

    if (post && post.number() === 1) {
      items.add('space-pocket-media', <MediaComposer composer={this} />, -20);
    }
  });

  extend(EditPostComposer.prototype, 'data', function (data) {
    const post = this.attrs.post;

    if (post && post.number() === 1) {
      data.spacePocketMedia = cloneMedia(this.spacePocketMedia);
      data.spacePocketTopics = cloneTopics(this.composer.spacePocketTopics);

      if (this.spacePocketMedia.length && (!data.content || !String(data.content).trim())) {
        data.content = IMAGE_ONLY_CONTENT;
      }
    }
  });

  override(EditPostComposer.prototype, 'hasChanges', function (original) {
    const post = this.attrs.post;
    const mediaChanged =
      post &&
      post.number() === 1 &&
      this.spacePocketOriginalMedia !== JSON.stringify(this.spacePocketMedia || []);
    const topicsChanged =
      post &&
      post.number() === 1 &&
      this.spacePocketOriginalTopics !== JSON.stringify(this.composer.spacePocketTopics || []);

    return original() || mediaChanged || topicsChanged;
  });

  override(EditPostComposer.prototype, 'onsubmit', function (original) {
    if (this.spacePocketMediaUploading) {
      app.alerts.show({ type: 'error' }, app.translator.trans('space-pocket-posts.forum.composer.wait_upload'));
      return;
    }

    this.spacePocketMediaSubmitted = true;
    this.composer.spacePocketTopicsSubmitted = true;
    return original();
  });

  extend(EditPostComposer.prototype, 'loaded', function () {
    this.spacePocketMediaSubmitted = false;
    this.composer.spacePocketTopicsSubmitted = false;
  });

  extend(CommentPost.prototype, 'content', function (content) {
    stripLegacyImageOnlyContent(content);

    const post = this.attrs.post;
    resolveUnknownUserLabels(content, {
      discussion: post && typeof post.discussion === 'function' ? post.discussion() : null,
      usernames: getReplyMentionUsernames(post),
    });

    if (!post || post.number() !== 1) return;

    // Flarum hides the post body when a visible post is deleted. Keep the
    // extension-owned gallery and topics hidden as well, while still allowing
    // moderators to reveal the complete post content when needed.
    if (post.isHidden() && !this.revealContent) return;

    const discussion = post.discussion();
    const media = cloneMedia(discussion && discussion.spacePocketMedia());
    const topics = cloneTopics(discussion && discussion.spacePocketTopics());
    const topicLinks = cloneTopicLinks(
      discussion && discussion.spacePocketTopicLinks && discussion.spacePocketTopicLinks(),
      topics
    );

    if (!content) return;

    const beforeContent = media.length ? [<MediaGallery media={media} discussion={discussion} />] : [];
    const afterContent =
      app.forum.attribute('spacePocketTopicsEnabled') && topicLinks.length ? [<TopicList topics={topicLinks} />] : [];

    if (!beforeContent.length && !afterContent.length) return;

    if (Array.isArray(content)) {
      content.unshift(...beforeContent);
      content.push(...afterContent);
    } else if (Array.isArray(content.children)) {
      content.children.unshift(...beforeContent);
      content.children.push(...afterContent);
    } else {
      content.children = [...beforeContent, content.children, ...afterContent];
    }
  });

  installForumProductShare();
}, -100);
